/**
 * Flash-Read Achievement System
 * 100 Creative Achievements organized by category
 */

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: AchievementCategory;
  rarity: AchievementRarity;
  requirement: string;
  xpReward: number;
  levelRequired?: number;  // Level needed to unlock this achievement
  levelReward?: number;    // Level gained when unlocked
}

export type AchievementCategory = 
  | 'speed' 
  | 'volume' 
  | 'streak' 
  | 'challenge' 
  | 'session' 
  | 'variety' 
  | 'time' 
  | 'social' 
  | 'milestone' 
  | 'secret';

export type AchievementRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'mythic';

export const ACHIEVEMENTS: Achievement[] = [
  // =====================
  // SPEED ACHIEVEMENTS (15)
  // =====================
  // Level 1 achievements (starter pack)
  {
    id: 'speed-100',
    name: 'Crawler',
    description: 'Read your first 100 words per minute',
    icon: '🐢',
    category: 'speed',
    rarity: 'common',
    requirement: 'Reach 100 WPM',
    xpReward: 10,
    levelRequired: 1,
  },
  {
    id: 'volume-1000',
    name: 'First Thousand',
    description: 'Read your first 1,000 words',
    icon: '1️⃣',
    category: 'volume',
    rarity: 'common',
    requirement: '1,000 words read',
    xpReward: 10,
    levelRequired: 1,
  },
  {
    id: 'challenge-1',
    name: 'Challenger',
    description: 'Complete your first daily challenge',
    icon: '🎯',
    category: 'challenge',
    rarity: 'common',
    requirement: '1 challenge completed',
    xpReward: 25,
    levelRequired: 1,
  },
  {
    id: 'milestone-level-5',
    name: 'Rising Star',
    description: 'Reach level 5',
    icon: '⭐',
    category: 'milestone',
    rarity: 'uncommon',
    requirement: 'Level 5',
    xpReward: 50,
    levelRequired: 1,
    levelReward: 5,
  },
  {
    id: 'speed-200',
    name: 'Walking Pace',
    description: 'Hit 200 words per minute',
    icon: '🚶',
    category: 'speed',
    rarity: 'common',
    requirement: 'Reach 200 WPM',
    xpReward: 25,
  },
  {
    id: 'speed-300',
    name: 'Jogger',
    description: 'Reach 300 words per minute',
    icon: '🏃',
    category: 'speed',
    rarity: 'uncommon',
    requirement: 'Reach 300 WPM',
    xpReward: 50,
  },
  {
    id: 'speed-400',
    name: 'Sprinter',
    description: 'Achieve 400 words per minute',
    icon: '💨',
    category: 'speed',
    rarity: 'uncommon',
    requirement: 'Reach 400 WPM',
    xpReward: 75,
  },
  {
    id: 'speed-500',
    name: 'Speed Demon',
    description: 'Blast through at 500 words per minute',
    icon: '⚡',
    category: 'speed',
    rarity: 'rare',
    requirement: 'Reach 500 WPM',
    xpReward: 100,
  },
  {
    id: 'speed-600',
    name: 'Bullet Train',
    description: 'Read at 600 words per minute',
    icon: '🚄',
    category: 'speed',
    rarity: 'rare',
    requirement: 'Reach 600 WPM',
    xpReward: 150,
  },
  {
    id: 'speed-700',
    name: 'Sound Barrier',
    description: 'Break the sound barrier in reading speed',
    icon: '🔊',
    category: 'speed',
    rarity: 'epic',
    requirement: 'Reach 700 WPM',
    xpReward: 200,
  },
  {
    id: 'speed-800',
    name: 'Teleport',
    description: 'Read faster than the eye can blink',
    icon: '✨',
    category: 'speed',
    rarity: 'epic',
    requirement: 'Reach 800 WPM',
    xpReward: 250,
  },
  {
    id: 'speed-900',
    name: 'Warp Drive',
    description: 'Engage maximum reading velocity',
    icon: '🚀',
    category: 'speed',
    rarity: 'legendary',
    requirement: 'Reach 900 WPM',
    xpReward: 350,
  },
  {
    id: 'speed-1000',
    name: 'Light Speed',
    description: 'Nothing can stop your reading speed',
    icon: '💫',
    category: 'speed',
    rarity: 'legendary',
    requirement: 'Reach 1000 WPM',
    xpReward: 500,
  },
  {
    id: 'speed-consistent-500',
    name: 'Consistent Speed',
    description: 'Maintain 500+ WPM for 10 sessions',
    icon: '⏱️',
    category: 'speed',
    rarity: 'rare',
    requirement: '10 sessions at 500+ WPM',
    xpReward: 150,
  },
  {
    id: 'speed-average-400',
    name: 'Above Average',
    description: 'Average 400+ WPM over 50 sessions',
    icon: '📊',
    category: 'speed',
    rarity: 'epic',
    requirement: 'Average 400+ WPM (50 sessions)',
    xpReward: 300,
  },
  {
    id: 'speed-improvement-100',
    name: 'Getting Faster',
    description: 'Improve your WPM by 100',
    icon: '📈',
    category: 'speed',
    rarity: 'uncommon',
    requirement: 'Improve WPM by 100',
    xpReward: 50,
  },
  {
    id: 'speed-improvement-200',
    name: 'Rapid Growth',
    description: 'Double your starting reading speed',
    icon: '📉',
    category: 'speed',
    rarity: 'rare',
    requirement: '2x starting WPM',
    xpReward: 150,
  },
  {
    id: 'speed-max-record',
    name: 'Personal Best',
    description: 'Set a new maximum WPM record',
    icon: '🏆',
    category: 'speed',
    rarity: 'common',
    requirement: 'Beat your previous max',
    xpReward: 25,
  },

  // New extended speed tiers (Phase 8)
  {
    id: 'speed-1100',
    name: 'Hyperspeed',
    description: 'Reach 1100 words per minute',
    icon: '🚀',
    category: 'speed',
    rarity: 'legendary',
    requirement: 'Reach 1100 WPM',
    xpReward: 600,
  },
  {
    id: 'speed-1200',
    name: 'Lightning Bolt',
    description: 'Read at 1200 words per minute',
    icon: '⚡',
    category: 'speed',
    rarity: 'legendary',
    requirement: 'Reach 1200 WPM',
    xpReward: 700,
  },
  {
    id: 'speed-1300',
    name: 'Warp Speed',
    description: 'Enter the warp zone at 1300 WPM',
    icon: '✨',
    category: 'speed',
    rarity: 'legendary',
    requirement: 'Reach 1300 WPM',
    xpReward: 800,
  },
  {
    id: 'speed-1400',
    name: 'Quantum Read',
    description: 'Quantum-level reading at 1400 WPM',
    icon: '🔮',
    category: 'speed',
    rarity: 'legendary',
    requirement: 'Reach 1400 WPM',
    xpReward: 900,
  },
  {
    id: 'speed-1500',
    name: 'Plasma Flow',
    description: 'Achieve 1500 WPM - reading becomes energy',
    icon: '💥',
    category: 'speed',
    rarity: 'mythic',
    requirement: 'Reach 1500 WPM',
    xpReward: 1500,
  },
  {
    id: 'speed-1600',
    name: 'Infinite Speed',
    description: 'Break the 1600 WPM barrier - beyond human limits',
    icon: '♾️',
    category: 'speed',
    rarity: 'mythic',
    requirement: 'Reach 1600 WPM',
    xpReward: 2000,
  },
  {
    id: 'speed-consistent-400',
    name: 'Steady 400',
    description: 'Maintain 400+ WPM for 10 sessions',
    icon: '📈',
    category: 'speed',
    rarity: 'uncommon',
    requirement: '10 sessions at 400+ WPM',
    xpReward: 100,
  },
  {
    id: 'speed-consistent-600',
    name: 'Bullet Train Pro',
    description: '10 sessions at 600+ WPM',
    icon: '🚄',
    category: 'speed',
    rarity: 'rare',
    requirement: '10 sessions at 600+ WPM',
    xpReward: 200,
  },
  {
    id: 'speed-consistent-700',
    name: 'Sonic Reader',
    description: '5 sessions at 700+ WPM',
    icon: '🔊',
    category: 'speed',
    rarity: 'epic',
    requirement: '5 sessions at 700+ WPM',
    xpReward: 300,
  },
  {
    id: 'speed-consistent-800',
    name: 'Teleport Pro',
    description: '5 sessions at 800+ WPM',
    icon: '✨',
    category: 'speed',
    rarity: 'epic',
    requirement: '5 sessions at 800+ WPM',
    xpReward: 400,
  },
  {
    id: 'speed-consistent-900',
    name: 'Warp Master',
    description: '5 sessions at 900+ WPM',
    icon: '🚀',
    category: 'speed',
    rarity: 'legendary',
    requirement: '5 sessions at 900+ WPM',
    xpReward: 500,
  },
  {
    id: 'speed-consistent-1000',
    name: 'Light Speed Pro',
    description: '3 sessions at 1000+ WPM',
    icon: '💫',
    category: 'speed',
    rarity: 'legendary',
    requirement: '3 sessions at 1000+ WPM',
    xpReward: 750,
  },
  {
    id: 'speed-consistent-1100',
    name: 'Hyperspeed Pro',
    description: '3 sessions at 1100+ WPM',
    icon: '🔮',
    category: 'speed',
    rarity: 'mythic',
    requirement: '3 sessions at 1100+ WPM',
    xpReward: 1000,
  },
  {
    id: 'speed-consistent-1200',
    name: 'Lightning Pro',
    description: '3 sessions at 1200+ WPM',
    icon: '⚡',
    category: 'speed',
    rarity: 'mythic',
    requirement: '3 sessions at 1200+ WPM',
    xpReward: 1500,
  },

  // =====================
  // VOLUME ACHIEVEMENTS (15)
  // =====================
  {
    id: 'volume-10000',
    name: 'Word Collector',
    description: 'Accumulate 10,000 words read',
    icon: '📚',
    category: 'volume',
    rarity: 'common',
    requirement: '10,000 words read',
    xpReward: 50,
  },
  {
    id: 'volume-50000',
    name: 'Avid Reader',
    description: 'Read 50,000 words - equivalent to a novel',
    icon: '📖',
    category: 'volume',
    rarity: 'uncommon',
    requirement: '50,000 words read',
    xpReward: 100,
  },
  {
    id: 'volume-100000',
    name: 'Century Club',
    description: 'Read 100,000 words',
    icon: '💯',
    category: 'volume',
    rarity: 'uncommon',
    requirement: '100,000 words read',
    xpReward: 150,
  },
  {
    id: 'volume-250000',
    name: 'Bookworm',
    description: 'Read 250,000 words - several novels',
    icon: '🐛',
    category: 'volume',
    rarity: 'rare',
    requirement: '250,000 words read',
    xpReward: 250,
  },
  {
    id: 'volume-500000',
    name: 'Literary Giant',
    description: 'Half a million words read',
    icon: '�🦀',
    category: 'volume',
    rarity: 'rare',
    requirement: '500,000 words read',
    xpReward: 350,
  },
  {
    id: 'volume-million',
    name: 'Million Word Club',
    description: 'Read one million words',
    icon: '🌟',
    category: 'volume',
    rarity: 'epic',
    requirement: '1,000,000 words read',
    xpReward: 500,
  },
  {
    id: 'volume-2million',
    name: 'Speed Reading Master',
    description: 'Two million words consumed',
    icon: '👑',
    category: 'volume',
    rarity: 'epic',
    requirement: '2,000,000 words read',
    xpReward: 750,
  },
  {
    id: 'volume-5million',
    name: 'Library of Congress',
    description: 'Five million words - an entire library',
    icon: '🏛️',
    category: 'volume',
    rarity: 'legendary',
    requirement: '5,000,000 words read',
    xpReward: 1000,
  },
  {
    id: 'volume-10million',
    name: 'Human Wikipedia',
    description: 'Ten million words read',
    icon: '🧠',
    category: 'volume',
    rarity: 'mythic',
    requirement: '10,000,000 words read',
    xpReward: 2000,
  },
  {
    id: 'volume-book-1',
    name: 'First Book',
    description: 'Complete reading your first book',
    icon: '📕',
    category: 'volume',
    rarity: 'uncommon',
    requirement: '1 book completed',
    xpReward: 75,
  },
  {
    id: 'volume-book-5',
    name: 'Book Enthusiast',
    description: 'Finish 5 complete books',
    icon: '📗',
    category: 'volume',
    rarity: 'rare',
    requirement: '5 books completed',
    xpReward: 150,
  },
  {
    id: 'volume-book-25',
    name: 'Bibliophile',
    description: 'Read 25 complete books',
    icon: '📘',
    category: 'volume',
    rarity: 'epic',
    requirement: '25 books completed',
    xpReward: 400,
  },
  {
    id: 'volume-book-100',
    name: 'Library Legend',
    description: 'Read 100 complete books',
    icon: '📙',
    category: 'volume',
    rarity: 'legendary',
    requirement: '100 books completed',
    xpReward: 1000,
  },
  {
    id: 'volume-book-500',
    name: 'Grand Library',
    description: '500 books - a lifetime of reading',
    icon: '📚',
    category: 'volume',
    rarity: 'mythic',
    requirement: '500 books completed',
    xpReward: 2500,
  },

  // =====================
  // VOLUME ACHIEVEMENTS (additional)
  // =====================
  {
    id: 'volume-books-1000',
    name: 'Bibliophile Supreme',
    description: 'Read 1000 complete books',
    icon: '📚',
    category: 'volume',
    rarity: 'mythic',
    requirement: '1000 books completed',
    xpReward: 5000,
  },
  {
    id: 'volume-day-100k',
    name: 'Daily Devourer',
    description: 'Read 100,000 words in a single day',
    icon: '🍽️',
    category: 'volume',
    rarity: 'legendary',
    requirement: '100k words in one day',
    xpReward: 1000,
  },
  {
    id: 'volume-15million',
    name: 'Living Library',
    description: 'Fifteen million words consumed',
    icon: '🏛️',
    category: 'volume',
    rarity: 'mythic',
    requirement: '15,000,000 words read',
    xpReward: 3000,
  },
  {
    id: 'volume-20million',
    name: 'Infinite Reader',
    description: 'Twenty million words and beyond',
    icon: '♾️',
    category: 'volume',
    rarity: 'mythic',
    requirement: '20,000,000 words read',
    xpReward: 5000,
  },

  // =====================
  // STREAK ACHIEVEMENTS (15)
  // =====================
  {
    id: 'streak-3',
    name: 'Getting Started',
    description: 'Read for 3 consecutive days',
    icon: '🔥',
    category: 'streak',
    rarity: 'common',
    requirement: '3-day streak',
    xpReward: 25,
  },
  {
    id: 'streak-7',
    name: 'Week Warrior',
    description: 'Maintain a 7-day reading streak',
    icon: '📅',
    category: 'streak',
    rarity: 'uncommon',
    requirement: '7-day streak',
    xpReward: 75,
  },
  {
    id: 'streak-14',
    name: 'Two Week Titan',
    description: 'Two weeks of consistent reading',
    icon: '🗓️',
    category: 'streak',
    rarity: 'uncommon',
    requirement: '14-day streak',
    xpReward: 125,
  },
  {
    id: 'streak-21',
    name: 'Three Week Champion',
    description: 'Three weeks of daily reading',
    icon: '🏅',
    category: 'streak',
    rarity: 'rare',
    requirement: '21-day streak',
    xpReward: 200,
  },
  {
    id: 'streak-30',
    name: 'Monthly Master',
    description: 'Read every day for a full month',
    icon: '🗓️',
    category: 'streak',
    rarity: 'rare',
    requirement: '30-day streak',
    xpReward: 300,
  },
  {
    id: 'streak-60',
    name: 'Bimonthly Beast',
    description: 'Two months of unbroken reading',
    icon: '🗓️',
    category: 'streak',
    rarity: 'epic',
    requirement: '60-day streak',
    xpReward: 500,
  },
  {
    id: 'streak-100',
    name: 'Century Streak',
    description: '100 days of reading - incredible!',
    icon: '💯',
    category: 'streak',
    rarity: 'epic',
    requirement: '100-day streak',
    xpReward: 750,
  },
  {
    id: 'streak-180',
    name: 'Half Year Hero',
    description: 'Six months of daily reading',
    icon: '🎖️',
    category: 'streak',
    rarity: 'legendary',
    requirement: '180-day streak',
    xpReward: 1000,
  },
  {
    id: 'streak-365',
    name: 'Year of Reading',
    description: 'Read every single day for a year',
    icon: '🏆',
    category: 'streak',
    rarity: 'mythic',
    requirement: '365-day streak',
    xpReward: 5000,
  },
  {
    id: 'streak-multiplier-2x',
    name: 'Double Trouble',
    description: 'Achieve a 2x streak multiplier',
    icon: '✖️',
    category: 'streak',
    rarity: 'rare',
    requirement: '2x streak multiplier',
    xpReward: 200,
  },
  {
    id: 'streak-multiplier-3x',
    name: 'Triple Threat',
    description: 'Reach a 3x streak multiplier',
    icon: '💥',
    category: 'streak',
    rarity: 'epic',
    requirement: '3x streak multiplier',
    xpReward: 400,
  },
  {
    id: 'streak-multiplier-5x',
    name: 'Multiplier Master',
    description: 'Achieve an incredible 5x multiplier',
    icon: '💎',
    category: 'streak',
    rarity: 'legendary',
    requirement: '5x streak multiplier',
    xpReward: 750,
  },
  {
    id: 'streak-recovery',
    name: 'Phoenix Rising',
    description: 'Recover a broken streak within 1 day',
    icon: '🔥',
    category: 'streak',
    rarity: 'uncommon',
    requirement: 'Resume streak same day',
    xpReward: 50,
  },
  {
    id: 'streak-bounce-back',
    name: 'Never Give Up',
    description: 'Start a new streak after a 7+ day gap',
    icon: '💪',
    category: 'streak',
    rarity: 'rare',
    requirement: 'New streak after 7+ days',
    xpReward: 100,
  },
  {
    id: 'streak-longest',
    name: 'Personal Record',
    description: 'Beat your longest streak',
    icon: '📊',
    category: 'streak',
    rarity: 'common',
    requirement: 'New personal best streak',
    xpReward: 50,
  },
  // New extended streak achievements (Phase 3)
  {
    id: 'streak-250',
    name: 'Quarter Master',
    description: 'Maintain a 250-day reading streak',
    icon: '🔮',
    category: 'streak',
    rarity: 'legendary',
    requirement: '250-day streak',
    xpReward: 1500,
  },
  {
    id: 'streak-400',
    name: '400-Day Wonder',
    description: 'Read every day for 400 days',
    icon: '🌟',
    category: 'streak',
    rarity: 'mythic',
    requirement: '400-day streak',
    xpReward: 3000,
  },
  {
    id: 'streak-500',
    name: 'Half Millennium',
    description: '500 consecutive days of reading',
    icon: '🏆',
    category: 'streak',
    rarity: 'mythic',
    requirement: '500-day streak',
    xpReward: 5000,
  },
  {
    id: 'streak-multiplier-10x',
    name: 'Tenfold Triumph',
    description: 'Achieve a 10x streak multiplier',
    icon: '💎',
    category: 'streak',
    rarity: 'mythic',
    requirement: '10x streak multiplier',
    xpReward: 2000,
  },

  // =====================
  // CHALLENGE ACHIEVEMENTS (15)
  // =====================
  {
    id: 'challenge-10',
    name: 'Challenge Seeker',
    description: 'Complete 10 daily challenges',
    icon: '🎯',
    category: 'challenge',
    rarity: 'uncommon',
    requirement: '10 challenges completed',
    xpReward: 75,
  },
  {
    id: 'challenge-50',
    name: 'Challenge Champion',
    description: 'Conquer 50 daily challenges',
    icon: '🏅',
    category: 'challenge',
    rarity: 'rare',
    requirement: '50 challenges completed',
    xpReward: 200,
  },
  {
    id: 'challenge-100',
    name: 'Challenge Master',
    description: 'Complete 100 daily challenges',
    icon: '👑',
    category: 'challenge',
    rarity: 'epic',
    requirement: '100 challenges completed',
    xpReward: 400,
  },
  {
    id: 'challenge-500',
    name: 'Challenge Legend',
    description: '500 challenges - unstoppable!',
    icon: '🌟',
    category: 'challenge',
    rarity: 'legendary',
    requirement: '500 challenges completed',
    xpReward: 1000,
  },
  {
    id: 'challenge-perfection',
    name: 'Perfect Week',
    description: 'Complete all 7 challenges in a week',
    icon: '💯',
    category: 'challenge',
    rarity: 'rare',
    requirement: '7 challenges in 7 days',
    xpReward: 200,
  },
  {
    id: 'challenge-perfection-month',
    name: 'Perfect Month',
    description: 'Complete all challenges in a month',
    icon: '🗓️',
    category: 'challenge',
    rarity: 'epic',
    requirement: 'All month challenges',
    xpReward: 500,
  },
  {
    id: 'challenge-speed-500',
    name: 'Speed Challenge',
    description: 'Complete a 500 WPM challenge',
    icon: '⚡',
    category: 'challenge',
    rarity: 'rare',
    requirement: '500 WPM challenge',
    xpReward: 150,
  },
  {
    id: 'challenge-volume-10k',
    name: 'Volume Challenge',
    description: 'Read 10,000 words in one day',
    icon: '📚',
    category: 'challenge',
    rarity: 'rare',
    requirement: '10,000 words in a day',
    xpReward: 150,
  },
  {
    id: 'challenge-marathon',
    name: 'Reading Marathon',
    description: 'Read for 60 minutes in one session',
    icon: '🏃',
    category: 'challenge',
    rarity: 'uncommon',
    requirement: '60-minute session',
    xpReward: 100,
  },
  {
    id: 'challenge-ultra-marathon',
    name: 'Ultra Marathon',
    description: 'Read for 2 hours continuously',
    icon: '🏅',
    category: 'challenge',
    rarity: 'epic',
    requirement: '120-minute session',
    xpReward: 250,
  },
  {
    id: 'challenge-hard',
    name: 'Hard Mode',
    description: 'Complete a challenge at hard difficulty',
    icon: '💀',
    category: 'challenge',
    rarity: 'uncommon',
    requirement: 'Hard difficulty challenge',
    xpReward: 100,
  },
  {
    id: 'challenge-night-owl',
    name: 'Night Owl',
    description: 'Complete a challenge after midnight',
    icon: '🦉',
    category: 'challenge',
    rarity: 'common',
    requirement: 'Challenge after midnight',
    xpReward: 25,
  },
  {
    id: 'challenge-early-bird',
    name: 'Early Bird',
    description: 'Complete a challenge before 6 AM',
    icon: '🐦',
    category: 'challenge',
    rarity: 'common',
    requirement: 'Challenge before 6 AM',
    xpReward: 25,
  },
  {
    id: 'challenge-streak-week',
    name: 'Challenge Streak',
    description: 'Complete challenges for 7 days straight',
    icon: '🔥',
    category: 'challenge',
    rarity: 'rare',
    requirement: '7-day challenge streak',
    xpReward: 200,
  },
  {
    id: 'challenge-2000',
    name: 'Challenge Legend',
    description: 'Complete 2000 daily challenges',
    icon: '🏆',
    category: 'challenge',
    rarity: 'legendary',
    requirement: '2000 challenges completed',
    xpReward: 3000,
  },
  {
    id: 'challenge-perfection-10',
    name: 'Perfect Decade',
    description: 'Achieve 10 perfect challenge days',
    icon: '💯',
    category: 'challenge',
    rarity: 'epic',
    requirement: '10 perfect days',
    xpReward: 500,
  },
  {
    id: 'challenge-perfection-50',
    name: 'Perfect Half-Century',
    description: 'Achieve 50 perfect challenge days',
    icon: '🌟',
    category: 'challenge',
    rarity: 'legendary',
    requirement: '50 perfect days',
    xpReward: 1500,
  },

  // =====================
  // SESSION ACHIEVEMENTS (10)
  // =====================
  {
    id: 'session-1',
    name: 'First Steps',
    description: 'Complete your first reading session',
    icon: '👣',
    category: 'session',
    rarity: 'common',
    requirement: '1 session',
    xpReward: 10,
  },
  {
    id: 'session-10',
    name: 'Getting Warmed Up',
    description: 'Complete 10 reading sessions',
    icon: '🔥',
    category: 'session',
    rarity: 'common',
    requirement: '10 sessions',
    xpReward: 30,
  },
  {
    id: 'session-50',
    name: 'Regular Reader',
    description: '50 sessions completed',
    icon: '📊',
    category: 'session',
    rarity: 'uncommon',
    requirement: '50 sessions',
    xpReward: 75,
  },
  {
    id: 'session-100',
    name: 'Century Session',
    description: '100 reading sessions',
    icon: '💯',
    category: 'session',
    rarity: 'rare',
    requirement: '100 sessions',
    xpReward: 150,
  },
  {
    id: 'session-500',
    name: 'Session Superstar',
    description: '500 sessions - dedication!',
    icon: '⭐',
    category: 'session',
    rarity: 'epic',
    requirement: '500 sessions',
    xpReward: 400,
  },
  {
    id: 'session-1000',
    name: 'Session Sage',
    description: 'A thousand sessions of reading',
    icon: '🧙',
    category: 'session',
    rarity: 'legendary',
    requirement: '1000 sessions',
    xpReward: 750,
  },
  {
    id: 'session-5000',
    name: 'Session Legend',
    description: '5000 sessions - absolutely insane!',
    icon: '🏆',
    category: 'session',
    rarity: 'mythic',
    requirement: '5000 sessions',
    xpReward: 2000,
  },
  {
    id: 'session-long-30',
    name: 'Half Hour Hero',
    description: 'A 30-minute reading session',
    icon: '⏰',
    category: 'session',
    rarity: 'common',
    requirement: '30-minute session',
    xpReward: 25,
  },
  {
    id: 'session-long-60',
    name: 'Hour of Power',
    description: 'A full hour of continuous reading',
    icon: '🕐',
    category: 'session',
    rarity: 'uncommon',
    requirement: '60-minute session',
    xpReward: 75,
  },
  {
    id: 'session-perfect-accuracy',
    name: 'Perfect Focus',
    description: '100% accuracy in a session',
    icon: '🎯',
    category: 'session',
    rarity: 'rare',
    requirement: '100% accuracy',
    xpReward: 100,
  },
  {
    id: 'session-no-pause',
    name: 'Flow State',
    description: 'Complete a reading session without pausing',
    icon: '🌊',
    category: 'session',
    rarity: 'uncommon',
    requirement: 'No pauses in session',
    xpReward: 50,
  },

  // =====================
  // VARIETY ACHIEVEMENTS (10)
  // =====================
  {
    id: 'variety-genres-3',
    name: 'Genre Explorer',
    description: 'Read 3 different content genres',
    icon: '🗺️',
    category: 'variety',
    rarity: 'common',
    requirement: '3 genres',
    xpReward: 25,
  },
  {
    id: 'variety-genres-10',
    name: 'World Traveler',
    description: 'Explore 10 different genres',
    icon: '🌍',
    category: 'variety',
    rarity: 'rare',
    requirement: '10 genres',
    xpReward: 150,
  },
  {
    id: 'variety-genres-25',
    name: 'Genre Polymath',
    description: 'Master 25 different genres',
    icon: '🎭',
    category: 'variety',
    rarity: 'epic',
    requirement: '25 genres',
    xpReward: 400,
  },
  {
    id: 'variety-sources-5',
    name: 'Source Collector',
    description: 'Read from 5 different content sources',
    icon: '📦',
    category: 'variety',
    rarity: 'common',
    requirement: '5 sources',
    xpReward: 30,
  },
  {
    id: 'variety-sources-20',
    name: 'Content Curator',
    description: 'Access 20 different sources',
    icon: '📚',
    category: 'variety',
    rarity: 'uncommon',
    requirement: '20 sources',
    xpReward: 75,
  },
  {
    id: 'variety-languages-2',
    name: 'Bilingual Reader',
    description: 'Read content in 2 languages',
    icon: '🌐',
    category: 'variety',
    rarity: 'rare',
    requirement: '2 languages',
    xpReward: 150,
  },
  {
    id: 'variety-languages-5',
    name: 'Polyglot Reader',
    description: 'Read in 5 different languages',
    icon: '🗣️',
    category: 'variety',
    rarity: 'epic',
    requirement: '5 languages',
    xpReward: 400,
  },
  {
    id: 'variety-time-morning',
    name: 'Morning Reader',
    description: 'Read during each time of day',
    icon: '🌅',
    category: 'variety',
    rarity: 'common',
    requirement: 'All times of day',
    xpReward: 25,
  },
  {
    id: 'variety-time-evening',
    name: 'Evening Reader',
    description: 'Read during morning, afternoon, and night',
    icon: '🌙',
    category: 'variety',
    rarity: 'uncommon',
    requirement: '3 time periods',
    xpReward: 50,
  },
  {
    id: 'variety-format-3',
    name: 'Format Flexible',
    description: 'Read 3 different file formats',
    icon: '📄',
    category: 'variety',
    rarity: 'common',
    requirement: '3 formats',
    xpReward: 25,
  },

  // New variety achievements (Phase 6)
  {
    id: 'variety-genres-12',
    name: 'Genre Connoisseur',
    description: 'Read 12 different content genres',
    icon: '🎭',
    category: 'variety',
    rarity: 'rare',
    requirement: '12 genres',
    xpReward: 200,
  },
  {
    id: 'variety-sources-50',
    name: 'Source Vault',
    description: 'Read from 50 different sources',
    icon: '🗄️',
    category: 'variety',
    rarity: 'rare',
    requirement: '50 sources',
    xpReward: 250,
  },
  {
    id: 'variety-sources-100',
    name: 'Source Oracle',
    description: 'Read from 100 different sources',
    icon: '🔮',
    category: 'variety',
    rarity: 'epic',
    requirement: '100 sources',
    xpReward: 500,
  },
  {
    id: 'variety-formats-5',
    name: 'Format Expert',
    description: 'Read 5 different file formats',
    icon: '📁',
    category: 'variety',
    rarity: 'uncommon',
    requirement: '5 formats',
    xpReward: 150,
  },
  {
    id: 'variety-time-night',
    name: 'Night Owl',
    description: 'Read during the night (10 PM - 5 AM)',
    icon: '🦉',
    category: 'variety',
    rarity: 'uncommon',
    requirement: 'Read at night',
    xpReward: 75,
  },
  {
    id: 'variety-time-weekend',
    name: 'Weekend Warrior',
    description: 'Read on both Saturday and Sunday',
    icon: '📅',
    category: 'variety',
    rarity: 'rare',
    requirement: 'Read on weekends',
    xpReward: 150,
  },

  // =====================
  // TIME ACHIEVEMENTS (10)
  // =====================
  {
    id: 'time-1hour',
    name: 'First Hour',
    description: 'Spend 1 hour total reading',
    icon: '⏱️',
    category: 'time',
    rarity: 'common',
    requirement: '1 hour total',
    xpReward: 10,
  },
  {
    id: 'time-10hours',
    name: 'Time Invested',
    description: '10 hours of reading time',
    icon: '🕐',
    category: 'time',
    rarity: 'common',
    requirement: '10 hours total',
    xpReward: 50,
  },
  {
    id: 'time-50hours',
    name: 'Time Well Spent',
    description: '50 hours of reading',
    icon: '⏳',
    category: 'time',
    rarity: 'uncommon',
    requirement: '50 hours total',
    xpReward: 150,
  },
  {
    id: 'time-100hours',
    name: 'Century of Hours',
    description: '100 hours read',
    icon: '🕰️',
    category: 'time',
    rarity: 'rare',
    requirement: '100 hours total',
    xpReward: 300,
  },
  {
    id: 'time-500hours',
    name: 'Time Lord',
    description: '500 hours of reading',
    icon: '👴',
    category: 'time',
    rarity: 'epic',
    requirement: '500 hours total',
    xpReward: 750,
  },
  {
    id: 'time-1000hours',
    name: 'Temporal Master',
    description: '1000 hours - over a month!',
    icon: '⏳',
    category: 'time',
    rarity: 'legendary',
    requirement: '1000 hours total',
    xpReward: 1500,
  },
  {
    id: 'time-daily-1hour',
    name: 'Hour Daily',
    description: 'Read for 1 hour in a single day',
    icon: '📆',
    category: 'time',
    rarity: 'uncommon',
    requirement: '1 hour in one day',
    xpReward: 50,
  },
  {
    id: 'time-daily-2hours',
    name: 'Two Hour Tour',
    description: '2 hours of reading in one day',
    icon: '📅',
    category: 'time',
    rarity: 'rare',
    requirement: '2 hours in one day',
    xpReward: 100,
  },
  {
    id: 'time-daily-4hours',
    name: 'Marathon Day',
    description: '4 hours of reading in a single day',
    icon: '🏃',
    category: 'time',
    rarity: 'epic',
    requirement: '4 hours in one day',
    xpReward: 250,
  },
   {
     id: 'time-week-20hours',
     name: 'Weekly Warrior',
     description: '20 hours of reading in a week',
     icon: '📅',
     category: 'time',
     rarity: 'rare',
     requirement: '20 hours in a week',
     xpReward: 200,
   },
   {
     id: 'time-daily-6hours',
     name: 'Six Hour Sprint',
     description: 'Read for 6 hours in a single day',
     icon: '🏃',
     category: 'time',
     rarity: 'legendary',
     requirement: '6 hours in one day',
     xpReward: 500,
   },
   {
     id: 'time-week-30hours',
     name: 'Weekly Champion',
     description: '30 hours of reading in a week',
     icon: '📊',
     category: 'time',
     rarity: 'epic',
     requirement: '30 hours in a week',
     xpReward: 400,
   },
   {
     id: 'time-month-100hours',
     name: 'Monthly Master',
     description: '100 hours of reading in a month',
     icon: '🗓️',
     category: 'time',
     rarity: 'legendary',
     requirement: '100 hours in a month',
     xpReward: 750,
   },

  // =====================
  // SOCIAL ACHIEVEMENTS (5)
  // =====================
  {
    id: 'social-leaderboard',
    name: 'On The Board',
    description: 'Appear on any leaderboard',
    icon: '📋',
    category: 'social',
    rarity: 'rare',
    requirement: 'Leaderboard placement',
    xpReward: 100,
  },
  {
    id: 'social-top-100',
    name: 'Top Century',
    description: 'Reach top 100 on any leaderboard',
    icon: '💯',
    category: 'social',
    rarity: 'epic',
    requirement: 'Top 100',
    xpReward: 250,
  },
  {
    id: 'social-top-10',
    name: 'Top Ten',
    description: 'Break into the top 10',
    icon: '🔟',
    category: 'social',
    rarity: 'legendary',
    requirement: 'Top 10',
    xpReward: 500,
  },
  {
    id: 'social-first',
    name: 'Number One',
    description: 'Reach the top of any leaderboard',
    icon: '🥇',
    category: 'social',
    rarity: 'mythic',
    requirement: '1st place',
    xpReward: 2000,
  },
  {
    id: 'social-share',
    name: 'Share Your Success',
    description: 'Share your achievements publicly',
    icon: '📢',
    category: 'social',
    rarity: 'common',
    requirement: 'Share achievement',
    xpReward: 10,
  },

  // =====================
  // MILESTONE ACHIEVEMENTS (5)
  // =====================
  {
    id: 'milestone-level-10',
    name: 'Peak Performer',
    description: 'Reach the maximum level 10',
    icon: '👑',
    category: 'milestone',
    rarity: 'legendary',
    requirement: 'Level 10',
    xpReward: 500,
  },
  {
    id: 'milestone-xp-10k',
    name: 'XP Hunter',
    description: 'Earn 10,000 total XP',
    icon: '🎯',
    category: 'milestone',
    rarity: 'rare',
    requirement: '10,000 XP',
    xpReward: 150,
  },
  {
    id: 'milestone-xp-100k',
    name: 'XP Master',
    description: 'Accumulate 100,000 XP',
    icon: '💎',
    category: 'milestone',
    rarity: 'epic',
    requirement: '100,000 XP',
    xpReward: 500,
  },
  {
    id: 'milestone-all-common',
    name: 'Completionist',
    description: 'Unlock all common achievements',
    icon: '🎖️',
    category: 'milestone',
    rarity: 'epic',
    requirement: 'All common',
    xpReward: 300,
  },
  {
    id: 'milestone-xp-500k',
    name: 'Half Million Hero',
    description: 'Accumulate 500,000 total XP',
    icon: '💎',
    category: 'milestone',
    rarity: 'legendary',
    requirement: '500,000 XP',
    xpReward: 2000,
  },
  {
    id: 'milestone-xp-1M',
    name: 'Million XP Master',
    description: 'Reach 1,000,000 total XP',
    icon: '👑',
    category: 'milestone',
    rarity: 'mythic',
    requirement: '1,000,000 XP',
    xpReward: 5000,
  },
  {
    id: 'milestone-achievements-50',
    name: 'Achievement Hunter',
    description: 'Unlock 50 achievements',
    icon: '🏆',
    category: 'milestone',
    rarity: 'epic',
    requirement: '50 achievements unlocked',
    xpReward: 1000,
  },

  // =====================
  // SECRET ACHIEVEMENTS (5)
  // =====================
  {
    id: 'secret-midnight',
    name: 'Midnight Reader',
    description: 'Read at exactly midnight',
    icon: '🕛',
    category: 'secret',
    rarity: 'rare',
    requirement: 'Read at midnight',
    xpReward: 75,
  },
  {
    id: 'secret-palindrome',
    name: 'Palindrome Party',
    description: 'Read at 12:21 or 2:42',
    icon: '🔢',
    category: 'secret',
    rarity: 'rare',
    requirement: 'Palindromic time',
    xpReward: 50,
  },
  {
    id: 'secret-420',
    name: '420 Speed',
    description: 'Read at exactly 420 WPM',
    icon: '🍃',
    category: 'secret',
    rarity: 'rare',
    requirement: '420 WPM',
    xpReward: 100,
  },
  {
    id: 'secret-first-of-month',
    name: 'New Month, New Goals',
    description: 'Read on the first of any month',
    icon: '📅',
    category: 'secret',
    rarity: 'uncommon',
    requirement: 'First of month',
    xpReward: 25,
  },
  {
    id: 'secret-leap-year',
    name: 'Leap Year Legend',
    description: 'Read on February 29th',
    icon: '🐸',
    category: 'secret',
    rarity: 'mythic',
    requirement: 'Feb 29',
    xpReward: 500,
  },
  // New secret achievements (Phase 7)
  {
    id: 'secret-christmas',
    name: 'Christmas Reader',
    description: 'Read on December 25th',
    icon: '🎄',
    category: 'secret',
    rarity: 'epic',
    requirement: 'Read on Dec 25',
    xpReward: 250,
  },
  {
    id: 'secret-friday-13',
    name: 'Unlucky 13',
    description: 'Read on Friday the 13th',
    icon: '🎃',
    category: 'secret',
    rarity: 'epic',
    requirement: 'Friday the 13th',
    xpReward: 300,
  },
  {
    id: 'secret-1111',
    name: '11:11 Wish',
    description: 'Read at exactly 11:11 AM or PM',
    icon: '🙏',
    category: 'secret',
    rarity: 'rare',
    requirement: '11:11 timestamp',
    xpReward: 100,
  },
  {
    id: 'secret-pi',
    name: 'Pi Time',
    description: 'Read at exactly 3:14 or 15:14',
    icon: '🥧',
    category: 'secret',
    rarity: 'rare',
    requirement: '3:14 or 15:14',
    xpReward: 100,
  },
  {
    id: 'secret-666',
    name: 'Number of the Beast',
    description: 'Read at exactly 666 WPM',
    icon: '😈',
    category: 'secret',
    rarity: 'legendary',
    requirement: '666 WPM',
    xpReward: 200,
  },
];

// XP Required per level
export const XP_PER_LEVEL = 500;

export const LEVELS = [
  { level: 1, title: 'Beginner', xpRequired: 0, color: 'from-green-400 to-green-600' },
  { level: 2, title: 'Apprentice', xpRequired: 500, color: 'from-blue-400 to-blue-600' },
  { level: 3, title: 'Reader', xpRequired: 1100, color: 'from-indigo-400 to-indigo-600' },
  { level: 4, title: 'Skilled', xpRequired: 1800, color: 'from-purple-400 to-purple-600' },
  { level: 5, title: 'Advanced', xpRequired: 2600, color: 'from-violet-400 to-violet-600' },
  { level: 6, title: 'Expert', xpRequired: 3500, color: 'from-pink-400 to-pink-600' },
  { level: 7, title: 'Master', xpRequired: 4500, color: 'from-red-400 to-red-600' },
  { level: 8, title: 'Grandmaster', xpRequired: 5600, color: 'from-orange-400 to-orange-600' },
  { level: 9, title: 'Legend', xpRequired: 6800, color: 'from-yellow-400 to-yellow-600' },
  { level: 10, title: 'Mythic', xpRequired: 8000, color: 'from-amber-400 to-amber-600' },
];

// Helper: Calculate level from total XP
export const calculateLevel = (totalXP: number): { level: number; title: string; progress: number; nextXP: number } => {
  for (let i = LEVELS.length - 1; i >= 0; i--) {
    if (totalXP >= LEVELS[i].xpRequired) {
      const currentLevel = LEVELS[i];
      const nextLevel = LEVELS[i + 1];
      if (nextLevel) {
        const progress = ((totalXP - currentLevel.xpRequired) / (nextLevel.xpRequired - currentLevel.xpRequired)) * 100;
        return {
          level: currentLevel.level,
          title: currentLevel.title,
          progress: Math.min(progress, 100),
          nextXP: nextLevel.xpRequired - totalXP,
        };
      }
      return { level: 10, title: currentLevel.title, progress: 100, nextXP: 0 };
    }
  }
  return { level: 1, title: LEVELS[0].title, progress: (totalXP / LEVELS[0].xpRequired) * 100, nextXP: LEVELS[0].xpRequired - totalXP };
};

// Helper: Get achievements for a specific level
export const getAchievementsForLevel = (level: number): Achievement[] => {
  return ACHIEVEMENTS.filter(a => (a.levelRequired || 1) <= level);
};

// Rarity colors
export const RARITY_COLORS: Record<AchievementRarity, string> = {
  common: 'text-slate-400 border-slate-300',
  uncommon: 'text-green-400 border-green-300',
  rare: 'text-blue-400 border-blue-300',
  epic: 'text-purple-400 border-purple-300',
  legendary: 'text-amber-400 border-amber-300',
  mythic: 'text-pink-400 border-pink-300',
};

// Category display names
export const CATEGORY_NAMES: Record<AchievementCategory, string> = {
  speed: 'Speed',
  volume: 'Volume',
  streak: 'Streaks',
  challenge: 'Challenges',
  session: 'Sessions',
  variety: 'Variety',
  time: 'Time',
  social: 'Social',
  milestone: 'Milestones',
  secret: 'Secret',
};
